import { CSSProperties, ReactNode } from 'react';
import { Tooltip } from '@mui/material';

import { classNames } from '../lib/classNames';

const colors = {
  gray: `bg-gray-100 text-gray-700 border-gray-200`,
  red: `bg-red-50 text-red-600 border-red-200`,
  green: `bg-emerald-50 text-emerald-600 border-emerald-200`,
  blue: `bg-blue-50 text-blue-700 border-blue-200`,
  indigo: `bg-indigo-100 text-indigo-700 border-indigo-200`,
  orange: `bg-orange-50 text-orange-700 border-orange-200`,
  amber: `bg-amber-50 text-amber-700 border-amber-200`,
  purple: `bg-purple-50 text-purple-700 border-purple-200`,
  paidniceGreen: `bg-paidniceGreen-50 text-paidniceGreen-600 border-paidniceGreen-200`,
  paidniceRed: `bg-paidniceRed-50 text-paidniceRed-600 border-paidniceRed-200`,
};

const sizes = {
  xs: `px-2 py-0.5 text-[10px]`,
  sm: `px-2 py-0.5 text-xs`,
  md: `px-2.5 py-1 text-sm`,
};

const dotColors = {
  gray: `bg-gray-400`,
  red: `bg-red-500`,
  green: `bg-emerald-500`,
  blue: `bg-blue-500`,
  indigo: `bg-indigo-500`,
  orange: `bg-orange-500`,
  amber: `bg-amber-500`,
  purple: `bg-purple-500`,
  paidniceGreen: `bg-paidniceGreen-500`,
  paidniceRed: `bg-paidniceRed-500`,
};

export interface IPillProps {
  message: string;
  color?: keyof typeof colors;
  size?: keyof typeof sizes;
  bordered?: boolean;
  dot?: boolean;
  tooltip?: string;
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
}

/** Rounded-full status pill — the soft-toned counterpart to Badge (which is
 * rounded-md with a ring). Use for inline statuses like "3 days overdue",
 * "Delivered" or "Xero synced". */
export function Pill({ message, color = `gray`, size = `sm`, bordered, dot, tooltip, className, style, children }: IPillProps) {
  const pill = (
    <span className={
      classNames(
        `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full font-medium`,
        colors[color],
        sizes[size],
        bordered ? `border` : ``,
        className,
      )
    }
    style={ style }
    >
      {
        dot && <span className={ classNames(`h-1.5 w-1.5 shrink-0 rounded-full`, dotColors[color]) }/>
      }
      { message }
      { children }
    </span>
  );

  if (tooltip) {
    return (
      <Tooltip title={ tooltip }>
        { pill }
      </Tooltip>
    );
  }

  return pill;
}
